import { Box, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as api from '../api';
import LoadingSpinner from '../components/LoadingSpinner';
import LoginLayout from '../components/LoginLayout';
import { LoginWithoutAccountForm } from '../components/LoginWithoutAccountForm';
import { BalanceContext } from '../context/BalanceContext';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';

export function BalancesQueryPage() {
  const [auth, setAuth] = useState({
    lastName: '',
    orderNumber: '',
    passportNoOrCitizenshipNumber: '',
  });
  const { setBalances } = useContext(BalanceContext);
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const nav = useNavigate();
  const showSnackbar = useAlertSnackbar();

  const handleSubmit = async (vals: {
    lastName: string;
    queryValue: string;
    queryType: 'orderNumber' | 'passportNoOrCitizenshipNumber';
  }) => {
    try {
      setSubmitting(true);

      const params =
        vals.queryType === 'orderNumber'
          ? { lastName: vals.lastName, orderNumber: vals.queryValue }
          : {
              lastName: vals.lastName,
              passportNoOrCitizenshipNumber: vals.queryValue,
            };

      const res = await api.balances(params);
      const updatedBalances = await Promise.all(
        res.map(async (item) => {
          // initial toPay fill
          let toPay = {
            amount: item.payment.balance.amount,
            currency: { id: 724, code: 'TRY', name: 'Turkish Lira' },
            currencyExchangeRate: 0,
          } as api.ApiAmountWithCurrency;

          if (toPay.amount > 0)
            toPay = await api.currencyExchange(
              item.payment.balance.currency.code,
              'TRY',
              item.payment.balance.amount,
            );

          return {
            ...item,
            payment: {
              ...item.payment,
              loyalty: {
                points: 0,
                fiat: 0,
              },
              received: {
                amount:
                  (item.payment.total.amount || 0) -
                  (item.payment.balance.amount || 0),
                currency: item.payment.balance.currency,
              },
              toPay: {
                amount: toPay?.amount || 0,
                currencyExchangeRate:
                  item.payment.balance.amount > 0
                    ? item.payment.balance.amount / toPay?.amount
                    : 0, // 1 TRY => ?€ | ?$...
                currency: toPay.currency || null,
              },
            },
          };
        }),
      );

      setBalances(updatedBalances);
      setAuth({
        ...auth,
        lastName: vals.lastName,
        orderNumber: vals.queryType === 'orderNumber' ? vals.queryValue : '',
        passportNoOrCitizenshipNumber:
          vals.queryType === 'passportNoOrCitizenshipNumber'
            ? vals.queryValue
            : '',
      });
      nav('/balances');
      setSubmitting(false);
    } catch (e: any) {
      showSnackbar(
        e.detail || e.message || t('balancesQueryPage.balancesNotFound'),
        'error',
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      {submitting && (
        <LoadingSpinner loadingText={t('balancesQueryPage.loading')} />
      )}
      <LoginLayout>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          {t('balancesQueryPage.title')}
        </Typography>
        <Box sx={{ maxWidth: '100%', mt: 2 }}>
          <LoginWithoutAccountForm
            initialValues={auth}
            onSubmit={handleSubmit}
          />
        </Box>
      </LoginLayout>
    </>
  );
}
